import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { prepareQuestionList } from "./question.const";
import classes from "./PrepareDiagnosis.module.scss";
import Link from "../../atoms/link/Link";
import { useNavigate } from "react-router-dom";
import {
  postUnregisteredUserResponse,
  postRegisteredUserResponse,
  PostUserResponseParam,
} from "../../api/UsersResponseApi";
import {
  postUnregisteredUsersSubmission,
  postRegisteredUsersSubmission,
} from "../../api/UsersSubmissionApi";

import BreadCrumb from "../../molecules/breadcrumb/BreadCrumb";
import PrepareQuestion from "../../organism/prepare-question/PrepareQuestion";
import PrepareCheckBox from "../../organism/prepare-checkbox/PrepareCheckBox";
import ProgressBar from "../../organism/progress-bar/ProgressBar";
import { useLoginStatus } from "../../common/hooks/useLoginStatus";
import PrepareLoading from "../../organism/prepare-loading/PrepareLoading";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import { CheckBoxItem } from "../../atoms/checkbox/CheckBox";
import charimage3 from "../../static/images/prepare/char-question-3.png";

// ロゴファイルの動的読み込み
const prePareImage = Array.from({ length: 6 }).map((_, index) => {
  const prepareNumber = index + 1;
  return require(`../../static/images/prepare/prepareQuizImage${prepareNumber}.png`);
});

const getPrepareImagePath = (currentQuiz: number | undefined) => {
  if (currentQuiz == null) {
    return prePareImage[0];
  }
  return prePareImage[currentQuiz];
};

// ---- アニメのタイミング定義（全体のテンポを調整）----
const QUESTION_DELAY = 0.4; // (1) 質問が出るまで
const IMAGE_DELAY = 0.4; // (2) 画像イメージが出るまで
const ANSWER_ITEM_DUR = 0.4; // (3)各回答1件のアニメduration
const ANSWER_STAGGER = 0.3; // (3)回答1件ずつの間隔（短くするほど速く出揃う）

// ローディングを見せる時間（TypeDiagnosis を参考に 5000ms）
const LOADING_MS = 5000;

const PrepareDiagnosis = () => {
  const [currentQuiz, setCurrentQuiz] = useState(1);
  const navigate = useNavigate();
  const [answer, setAnswer] = useState<{ [key: string]: string }>({});
  const postUnregistered = postUnregisteredUserResponse();
  const postRegistered = postRegisteredUserResponse();
  const postSubmission = postRegisteredUsersSubmission();
  const postUnregisteredSubmission = postUnregisteredUsersSubmission();

  const [submitState, setSubmitState] = useState(false);
  const { isLoggedIn } = useLoginStatus();


  // ローディング制御
  const [isLoading, setIsLoading] = useState(false);

  // 質問ステップかチェックリストステップかを判定するフラグ
  const [isCheckStep, setIsCheckStep] = useState(false);

  // チェックボックスの選択状態を親に保持
  const [checkboxForm, setCheckboxForm] = useState<{ [key: string]: 1 | 0 }>({});
  // チェックボックスが選択済みか未選択かの判定項目
  const isAnyCheckboxSelected = Object.values(checkboxForm).some((v) => v === 1);

  // 現在の設問IDと復元済みの選択
  const currentQuestionId = prepareQuestionList[currentQuiz - 1].questionNo;
  const selectedNoForCurrent =
    answer[String(currentQuestionId)] != null
      ? parseInt(answer[String(currentQuestionId)], 10)
      : 0;

  // ---- アニメ時間設計 ----
  const answersCount = prepareQuestionList[currentQuiz - 1].answerList.length;
  const answersStartDelay = QUESTION_DELAY + IMAGE_DELAY + ANSWER_ITEM_DUR; // (2)の0.5s後に回答開始
  const lastAnswerStart = answersStartDelay + Math.max(0, answersCount - 1) * ANSWER_STAGGER;
  const commentsDelay = lastAnswerStart + ANSWER_ITEM_DUR;
  
  // 二重発火ガード（同じ質問IDは1回だけ送る）
  const sent = useRef<Set<string>>(new Set());
  // DOMに設定するID
  const dataQuestionId = `Q${
    prepareQuestionList[currentQuiz - 1]?.questionNo ?? currentQuiz
  }`;

  const hasPosted = useRef(false); 
  useEffect(() => {

    // 初回のみ実行
    if (hasPosted.current) return;
    hasPosted.current = true;

    if (isLoggedIn) {
      postSubmission.post({
        simulationType: "light",
      });
    } else {
      postUnregisteredSubmission.post({
        simulationType: "light",
      });
    }  
  },[]);

  useEffect(() => {
    const item = prepareQuestionList[currentQuiz - 1];
    if (!item) return;

    // 'Q{questionNo}' 形式で安定化（例：Q1〜Q6）
    const qId = `Q${item.questionNo ?? currentQuiz}`;

    // すでに送っていればスキップ（アニメ/再レンダ対策）
    if (sent.current.has(qId)) return;
    sent.current.add(qId);

    // dataLayer存在保証 → イベント送信
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: "question_visible",
      question_id: qId, // ← 固定文字列ではなく“現在の質問ID”を動的に送る
    });
  }, [currentQuiz]);

  const answerCallback = (questionNo: number, answerNo: number) => {
    const tmpAnswer = {
      ...answer,
      [questionNo.toString()]: answerNo.toString(),
    };

    // ★ 最後の設問かどうか判定
    if (prepareQuestionList.length === currentQuiz) {
      // 最後の設問：回答だけ保存してチェックボックスステップに遷移
      setAnswer(tmpAnswer);
      setIsCheckStep(true);
      // ※ このタイミングではまだ API 送信しない
    } else {
      // 通常の次の設問へ
      setAnswer(tmpAnswer);
      setCurrentQuiz((prev) => prev + 1);
    }
  };

  const prevCallback = () => {
    // ★ チェックボックス画面の場合は「質問画面（最後の設問）」に戻す
    if (isCheckStep) {
      setIsCheckStep(false);
      return;
    }

    // ★ 通常の質問ステップの場合は、1つ前の設問へ
    setCurrentQuiz((prev) => (prev > 1 ? prev - 1 : prev));
  };

  // ★ チェックボックス変更時に親の state を更新
  const handleCheckboxChange = (item: CheckBoxItem) => {
    setCheckboxForm((prev) => ({
      ...prev,
      [item.id]: item.value ? 1 : 0,
    }));
  };

  const handleResultClick = () => {
    
    // ① 通常の設問の回答
    const normalResponses = Object.keys(answer).map((key) => ({
      questionId: key,
      choiceId: answer[key],
    }));

    // ② チェックボックスの回答(PreapreCheckboxのIDと一致)
    const ALL_CHECK_IDS = ["1","2","3","4","5","6","7","8","9","10","99"];
    const checkboxResponses = ALL_CHECK_IDS.map((id) => ({
      checkId: id,
      isChecked: (checkboxForm[id] ?? 0).toString(), // 未定義なら "0" 扱い
    }));

    const payload: PostUserResponseParam = {
      responseList: normalResponses,  // 質問結果
      checkList: checkboxResponses,   // チェックリスト結果
    };

  const checkedCheckIds: string[] = checkboxResponses
    .filter((r) => r.isChecked === "1")
    .map((r) => r.checkId);
  (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: 'checklist_shown',
      checklistItems: checkedCheckIds
    });

    setSubmitState(true);
    setIsLoading(true);

    if (isLoggedIn) {
      postRegistered.post(payload);
    } else {
      postUnregistered.post(payload);
    }
  };

  // ★ ローディング後の画面遷移（API成功を検知してから LOADING_MS 待つ）
  useEffect(() => {
    if (!submitState) return;

    let timer: number | undefined;

    if (isLoggedIn) {
      if (postRegistered.state?.data != null && postRegistered.state?.status === "success") {
        timer = window.setTimeout(() => {
          setIsLoading(false);
          navigate("/familynavi/register");
        }, LOADING_MS);
      }
    } else {
      if (postUnregistered.state?.data != null && postUnregistered.state?.status === "success") {
        timer = window.setTimeout(() => {
          setIsLoading(false);
          navigate("/familynavi/register");
        }, LOADING_MS);
      }
    }

    return () => {
      if (timer != null) window.clearTimeout(timer);
    };
  }, [submitState, postRegistered.state, postUnregistered.state, isLoggedIn, navigate]);

  // ★ ローディング表示切替（丸ごとローディングに切り替える）
  if (isLoading) {
    return <PrepareLoading />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.body}>
        {/* ★ 質問ステップ（Q1〜Qn）のときだけ表示 */}
        {!isCheckStep && (
          <>
            <ProgressBar max={prepareQuestionList.length} current={currentQuiz} />

            {/* 質問テキスト（下からスライド） */}
            <motion.div
              className={classes.question}
              key={`q-${currentQuiz}`}
              initial={{ y: "20vh", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: QUESTION_DELAY }}
            >
              {Array.isArray(prepareQuestionList[currentQuiz - 1].question) ? (
                (prepareQuestionList[currentQuiz - 1].question as string[]).map((q, i) => (
                  <div key={`q-line-${i}`}>{q}</div>
                ))
              ) : (
                <div>{prepareQuestionList[currentQuiz - 1].question as any}</div>
              )}
            </motion.div>

            {/* 画像も質問の後に下からスライド */}
            <motion.div
              className={classes.prepareImage}
              key={`img-${currentQuiz}`}
              initial={{ y: "30vh", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: QUESTION_DELAY + IMAGE_DELAY }}
            >
              <img src={getPrepareImagePath(currentQuiz - 1)} alt="prepareImage" />
            </motion.div>

            {/* 回答リスト & コメント表示 */}
            <div className={classes.content} data-question-id={dataQuestionId}>
              <PrepareQuestion
                key={`pq-${currentQuiz}`}
                totalQuestionNo={prepareQuestionList.length}
                currentQuestionNo={currentQuiz}
                answerCallback={answerCallback}
                content={prepareQuestionList[currentQuiz - 1]}
                hidePrev={currentQuiz === 1}
                selectedNo={selectedNoForCurrent}
                answersStartDelay={answersStartDelay}
                commentsDelay={commentsDelay}
                answerStagger={ANSWER_STAGGER}
                answerItemDur={ANSWER_ITEM_DUR}
              />
            </div>
          </>
        )}

        {/* ★ チェックボックスステップのときの画面 */}
        {isCheckStep && (
          <>
            <div className={classes.content}>
              <PrepareCheckBox
                checkboxForm={checkboxForm}
                onChange={handleCheckboxChange}
              />
            </div>
          </>
        )}

        {/* ★ 「ひとつ前に戻る」：質問ステップでは2問目以降、チェックステップでは常に表示 */}
        <div className={classes.bottomBlock}>
          {(
            (!isCheckStep && currentQuiz > 1) || // 質問ステップで2問目以降
            isCheckStep // チェックボックスステップ
          ) && <Link label={"ひとつ前に戻る"} onClick={prevCallback} />}
        </div>
        {isCheckStep && (
          <>
            <div className={classes.buttonArea}>
              <img className={classes.teacherOpen} src={charimage3} alt="" />
              <SimpleButton
                width="90%"
                label="結果を確認する"
                onClick={handleResultClick}
                disabled={!isAnyCheckboxSelected}
                />
            </div>
          </>
        )}
        <BreadCrumb
          data={[
            { displayName: "みらいいコンシェル", url: "/top" },
            { displayName: "家族の備えナビ"},
          ]}
        />
      </div>
    </div>
  );
};

export default PrepareDiagnosis;
